import { yupResolver } from '@hookform/resolvers/yup';
import { Container, Dialog, DialogContent, DialogTitle, Grid } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { Block } from 'src/components/Block';
import AppButton from 'src/components/common/AppButton';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import RHFRadioGroup from 'src/components/hook-form/rhf-radio-group';
import { StartHearingOption } from 'src/constants/start-hearing-option';
import { useJoinHearingCouncilWithLinkMutation } from 'src/graphql';
import { useLocales } from 'src/locales';
import useSchemas from 'src/schemas/hooks/useSchemas';
import { JoinHearingMutationVariablesType } from 'src/types/hearing-council';

export type JoinHearingPropsType = {
  open: boolean;
  onClose: VoidFunction;
  hearingCouncilId: string;
  join: any;
  isJoining: boolean;
};

const JoinHearingDialog = ({
  open,
  onClose,
  hearingCouncilId,
  join,
  isJoining,
}: JoinHearingPropsType) => {
  if (!open) return <></>;
  // #region States
  const queryClient = useQueryClient();
  const { t } = useLocales();
  const { JoinHearingSchema } = useSchemas();
  // #endregion States

  const methods = useForm<JoinHearingMutationVariablesType>({
    resolver: yupResolver(JoinHearingSchema),
  });
  // #endregion form

  // #region Services
  const { mutate: joinWithLink, isLoading: isJoinWithLink } = useJoinHearingCouncilWithLinkMutation(
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['HearingCouncils'] });
        onClose();
      },
    }
  );
  // #endregion Services

  const {
    handleSubmit,
    formState: { errors },
  } = methods;

  const onSubmit = useCallback(async (data: JoinHearingMutationVariablesType) => {
    joinWithLink({ youtubeLink: data?.linkMeet, hearingCouncilId: hearingCouncilId });
  }, []);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', padding: 4 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'center' }}>
          {t('dialogs.joinHearing.header')}
        </DialogTitle>
        <DialogContent>
          <Container>
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
              <Grid container rowSpacing={2} columnSpacing={{ xs: 0, md: 2 }}>
                <Grid item xs={12}>
                  <RHFRadioGroup
                    name="option"
                    options={StartHearingOption(t)}
                    sx={{ display: 'flex', flexDirection: 'row' }}
                  />
                </Grid>
                {methods.watch('option') == '2' && (
                  <Grid item xs={12}>
                    <Block label={t('dialogs.joinHearing.linkMeet')}>
                      <RHFTextField name="linkMeet" />
                    </Block>
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <AppButton
                    label={t('sessions.card.startNow')}
                    disabled={methods.watch('option') !== '1'}
                    fullWidth
                    onClick={() => join({ hearingCouncilId: hearingCouncilId })}
                    isLoading={isJoining}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <AppButton
                    // onClick={() => onSubmit(methods.watch())}
                    type="submit"
                    label={t('buttons.startSharing')}
                    disabled={methods.watch('option') !== '2'}
                    isLoading={isJoinWithLink}
                    fullWidth
                    variant="outlined"
                  />
                </Grid>
              </Grid>
            </FormProvider>
          </Container>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default JoinHearingDialog;
